import * as React from "react";
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { ChevronDown, Truck, RotateCcw, ShieldAlert } from "lucide-react";
import { cn } from "@/lib/utils";

type CartAssuranceItem = {
  id: string;
  title: string;
  summary?: string;
  icon: React.ComponentType<{ className?: string }>;
  content?: React.ReactNode;
};

const items: CartAssuranceItem[] = [
  {
    id: "shipping",
    title: "Vi sender hver mandag",
    summary:
      "Din ordre pakkes omhyggeligt og sendes fra vores showroom på Frederiksberg.",
    icon: Truck,
  },
  {
    id: "returns",
    title: "14 dages returret",
    summary: "Nem returnering, hvis du fortryder dit køb.",
    icon: RotateCcw,
    content: (
      <div className="space-y-2">
        <p>Du har 14 dages returret fra den dag, du modtager din ordre.</p>
        <p>
          Ved returnering køber du selv en returlabel og sender varen tilbage
          til os.
        </p>
      </div>
    ),
  },
  {
    id: "damage",
    title: "Transportskade? Vi hjælper dig",
    summary: "Vi håndterer kommunikationen med GLS.",
    icon: ShieldAlert,
    content: (
      <div className="space-y-2">
        <p>
          Hvis din ordre bliver beskadiget under transporten, hjælper vi med
          reklamationen og håndterer kommunikationen med GLS.
        </p>
        <p>
          Inden du smider emballagen ud, anbefaler vi, at du tager billeder af:
        </p>
        <ul className="list-disc space-y-1 pl-5">
          <li>Ydersiden af pakken</li>
          <li>Forsendelseslabelen</li>
          <li>Varens indpakning og pakkefyld</li>
          <li>Den beskadigede vare</li>
        </ul>
        <p>Gem emballagen, indtil sagen er afklaret.</p>
      </div>
    ),
  },
];

function StaticRow({ item }: { item: CartAssuranceItem }) {
  const Icon = item.icon;
  return (
    <div className="flex gap-3 py-3">
      <Icon className="mt-0.5 h-4 w-4 flex-shrink-0 text-foreground/70" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-foreground">{item.title}</p>
        {item.summary && (
          <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
            {item.summary}
          </p>
        )}
      </div>
    </div>
  );
}

function ExpandableRow({ item }: { item: CartAssuranceItem }) {
  const Icon = item.icon;
  return (
    <AccordionPrimitive.Item value={item.id}>
      <AccordionPrimitive.Header className="flex">
        <AccordionPrimitive.Trigger
          className={cn(
            "group flex w-full items-start gap-3 py-3 text-left",
            "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
          )}
        >
          <Icon className="mt-0.5 h-4 w-4 flex-shrink-0 text-foreground/70" aria-hidden="true" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-foreground">{item.title}</p>
            {item.summary && (
              <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
                {item.summary}
              </p>
            )}
          </div>
          <ChevronDown
            className="mt-1 h-4 w-4 flex-shrink-0 text-muted-foreground transition-transform duration-200 group-data-[state=open]:rotate-180"
            aria-hidden="true"
          />
        </AccordionPrimitive.Trigger>
      </AccordionPrimitive.Header>
      <AccordionPrimitive.Content
        className={cn(
          "overflow-hidden text-xs leading-relaxed text-muted-foreground",
          "data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down",
        )}
      >
        <div className="pb-4 pl-7 pr-2">{item.content}</div>
      </AccordionPrimitive.Content>
    </AccordionPrimitive.Item>
  );
}

export function CartAssurance() {
  const staticItems = items.filter((i) => !i.content);
  const expandableItems = items.filter((i) => i.content);
  return (
    <div className="divide-y divide-border border-y border-border">
      {staticItems.map((item) => (
        <StaticRow key={item.id} item={item} />
      ))}
      <AccordionPrimitive.Root
        type="single"
        collapsible
        className="divide-y divide-border"
      >
        {expandableItems.map((item) => (
          <ExpandableRow key={item.id} item={item} />
        ))}
      </AccordionPrimitive.Root>
    </div>
  );
}
